'use client'

import { useState, useEffect, Fragment, useCallback } from 'react';
import { useParams, Outlet, useLocation } from 'react-router-dom';
import CaseHomeNotesTasks from '../CaseHomeNotesTasks';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
} from '@headlessui/react'
import {
  Cog6ToothIcon,
  HomeIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  XMarkIcon,
  ChevronDownIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import { filingDebtorNames } from '../../utils/debtors';
import FirmTopBar from './FirmTopBar';

const API_URL = process.env.REACT_APP_API_URL;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function CaseLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { caseId } = useParams();
  const location = useLocation();
  const [caseInfo, setCaseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formsOpen, setFormsOpen] = useState(false);
  const [motionsOpen, setMotionsOpen] = useState(false);
  const [lettersOpen, setLettersOpen] = useState(false);
  const [quickNotesOpen, setQuickNotesOpen] = useState(false);

  const updateCaseInfo = useCallback((update) => {
    setCaseInfo(current => {
      if (typeof update === 'function') return update(current);
      if (!current) return update;
      return { ...current, ...update };
    });
  }, []);

  const refreshCaseInfo = useCallback(async ({ showLoading = false, signal } = {}) => {
    if (showLoading) setLoading(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`${API_URL}/firm-cases/${caseId}/`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        signal,
      });

      if (res.ok) {
        setCaseInfo(await res.json());
        return true;
      }
      console.error(`Failed to load case (${res.status})`);
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
    } finally {
      if (showLoading) setLoading(false);
    }
    return false;
  }, [caseId]);

  useEffect(() => {
    const controller = new AbortController();
    refreshCaseInfo({ showLoading: true, signal: controller.signal });
    return () => controller.abort();
  }, [location.pathname, refreshCaseInfo]);

  useEffect(() => {
    const refreshOnFocus = () => refreshCaseInfo();
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [refreshCaseInfo]);

  // main nav order: Dashboard, Case Home, Creditors, Forms, Documents...
  const caseNavigation = [
    { name: 'Case Home', href: `/case/${caseId}`, icon: FolderIcon },
    { name: 'Creditors', href: `/case/${caseId}/creditors`, icon: FolderIcon },
    { name: 'Credit Report', href: `/case/${caseId}/credit-report`, icon: FolderIcon },
    { name: 'One View', href: `/case/${caseId}/oneview`, icon: FolderIcon },
    // form preparation is expandable
    { name: 'Form Preparation', href: null, icon: DocumentDuplicateIcon },
    { name: 'Motions', href: null, icon: DocumentDuplicateIcon },
    { name: 'Documents', href: `/case/${caseId}/documents`, icon: DocumentTextIcon },
    { name: 'Communications', href: `/case/${caseId}/email`, icon: EnvelopeIcon },
    { name: 'Letters', href: null, icon: EnvelopeIcon },
    { name: 'Trustee Docs', href: `/case/${caseId}/trustee-docs`, icon: FolderIcon },
    { name: 'Dockets', href: `/case/${caseId}/dockets`, icon: FolderIcon },
    { name: 'Hearings', href: `/case/${caseId}/hearings`, icon: FolderIcon },
    { name: 'Timeline', href: `/case/${caseId}/timeline`, icon: CalendarDaysIcon },
    { name: 'Payment', href: `/case/${caseId}/payment`, icon: CurrencyDollarIcon },
    { name: 'Retainer', href: `/case/${caseId}/retainer`, icon: CurrencyDollarIcon },
  ];

  const formPages = [
    { name: 'Voluntary Petition', href: `/case/${caseId}/forms/voluntary-petition` },
    { name: 'Schedule A/B/C', href: `/case/${caseId}/forms/schedule-a-bc` },
    { name: 'Schedule D/E/F/G/H', href: `/case/${caseId}/forms/schedule-d-e-f` },
    { name: 'Schedule I/J/122', href: `/case/${caseId}/forms/schedule-ij` },
    { name: 'SOFA', href: `/case/${caseId}/forms/sofa` },
    { name: 'Chapter 13 Plan', href: `/case/${caseId}/forms/chapter-13-plan` },
    { name: 'All Forms', href: `/case/${caseId}/forms/all` },
    { name: 'Reaffirmations', href: `/case/${caseId}/reaffirmations` },
  ];

  const motionPages = [
    { name: 'Wage Order', href: `/case/${caseId}/forms/wage-order` },
    { name: 'Reaffirmation Extension', href: `/case/${caseId}/motions/reaffirmation-extension` },
    { name: 'Avoid Judicial Lien', href: `/case/${caseId}/motions/avoid-lien` },
  ];

  const letterPages = [
    { name: 'Omitted Creditor', href: `/case/${caseId}/letters/omitted` },
    {
      name: 'Garnishment Notice & Demand',
      href: `/case/${caseId}/letters/garnishment`,
    },
  ];

  return (
    <div>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 flex z-40 lg:hidden" onClose={setSidebarOpen}>
          <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-25" />
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel className="relative flex-1 flex flex-col max-w-xs w-full bg-white p-4">
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-800"
                >
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-10 border-b border-gray-200 pb-4">
                <div className="text-base font-bold leading-snug text-gray-900">
                  {caseInfo ? filingDebtorNames(caseInfo.debtors) || 'Unnamed client' : 'Loading case…'}
                </div>
                {caseInfo?.case_number && (
                  <div className="mt-1 text-sm font-semibold text-indigo-700">
                    Case {caseInfo.case_number}
                  </div>
                )}
                {caseInfo?.chapter && (
                  <div className="mt-1 text-xs text-gray-500">Chapter {caseInfo.chapter}</div>
                )}
              </div>
              <nav className="mt-4">
                <a
                  href="/dashboard"
                  className="flex items-center px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"
                >
                  <HomeIcon className="h-6 w-6 mr-3" aria-hidden="true" />
                  Firm Dashboard
                </a>
                {caseNavigation.map(item => {
                  if (item.name === 'Form Preparation') {
                    return (
                      <div key={item.name}>
                        <button
                          onClick={() => setFormsOpen(!formsOpen)}
                          className="flex items-center px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 w-full"
                        >
                          <DocumentDuplicateIcon className="h-6 w-6 mr-3" aria-hidden="true" />
                          {item.name}
                          <ChevronDownIcon className={`ml-auto h-5 w-5 transition-transform ${formsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {formsOpen && (
                          <div className="ml-8 mt-1 space-y-1">
                            {formPages.map(fp => (
                              <a
                                key={fp.name}
                                href={fp.href}
                                className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                              >
                                {fp.name}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  if (item.name === 'Letters') {
                    return (
                      <div key={item.name}>
                        <button
                          onClick={() => setLettersOpen(!lettersOpen)}
                          className="flex items-center px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 w-full"
                        >
                          <EnvelopeIcon className="h-6 w-6 mr-3" aria-hidden="true" />
                          {item.name}
                          <ChevronDownIcon className={`ml-auto h-5 w-5 transition-transform ${lettersOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {lettersOpen && (
                          <div className="ml-8 mt-1 space-y-1">
                            {letterPages.map(lp => (
                              <a
                                key={lp.name}
                                href={lp.href}
                                className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                              >
                                {lp.name}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  if (item.name === 'Motions') {
                    return (
                      <div key={item.name}>
                        <button
                          onClick={() => setMotionsOpen(!motionsOpen)}
                          className="flex items-center px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 w-full"
                        >
                          <DocumentDuplicateIcon className="h-6 w-6 mr-3" aria-hidden="true" />
                          {item.name}
                          <ChevronDownIcon className={`ml-auto h-5 w-5 transition-transform ${motionsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {motionsOpen && (
                          <div className="ml-8 mt-1 space-y-1">
                            {motionPages.map(mp => (
                              <a
                                key={mp.name}
                                href={mp.href}
                                className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                              >
                                {mp.name}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <a
                      key={item.name}
                      href={item.href || '#'}
                      className="flex items-center px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"
                    >
                      <item.icon className="h-6 w-6 mr-3" aria-hidden="true" />
                      {item.name}
                    </a>
                  );
                })}
              </nav>
            </DialogPanel>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" aria-hidden="true" />
        </Dialog>
      </Transition.Root>

      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 bg-slate-50 lg:flex lg:w-72 lg:flex-col border-r border-gray-200 px-6 pb-4">
        <div className="flex h-20 flex-col items-start justify-center">
          <h2 className={classNames(
            'text-lg font-bold leading-tight transition-opacity duration-500',
            caseInfo ? 'opacity-100' : 'opacity-0'
          )}>
            {caseInfo
              ? `${filingDebtorNames(caseInfo.debtors) || 'Unnamed client'} | Chapter ${caseInfo.chapter}`
              : 'Loading Case...'}
          </h2>
          {caseInfo?.case_number && (
            <p className="mt-1 text-sm font-semibold text-indigo-700">
              Case {caseInfo.case_number}
            </p>
          )}
        </div>
        <nav className="flex flex-1 flex-col mt-4">
          <ul className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul className="-mx-2 space-y-1">
                {/* Firm Dashboard - offset further left */}
                <li>
                  <a href="/dashboard" className="group flex gap-x-3 -ml-4 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-indigo-600">
                    <HomeIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600"/>
                    Firm Dashboard
                  </a>
                </li>

                {caseNavigation.map(item => (
                  item.name === 'Form Preparation' ? (
                    <li key={item.name}>
                      <button
                        onClick={() => setFormsOpen(!formsOpen)}
                        className={classNames(
                          'group flex w-full gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-indigo-600',
                          formsOpen ? 'bg-blue-50 text-indigo-600' : ''
                        )}
                      >
                        <DocumentDuplicateIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600"/>
                        {item.name}
                        <ChevronDownIcon className={classNames('ml-auto w-5 h-5 transition-transform', formsOpen ? 'rotate-180' : '')}/>
                      </button>
                      {formsOpen && (
                        <ul className="mt-1 space-y-1">
                          {formPages.map(fp => (
                            <li key={fp.name}>
                              <a
                                href={fp.href}
                                className={classNames(
                                  'group flex gap-x-3 pl-10 scale-95 rounded-md p-1 text-sm font-medium text-gray-600 hover:bg-blue-50 hover:text-indigo-600',
                                  location.pathname === fp.href ? 'bg-blue-100 text-indigo-600' : ''
                                )}
                              >
                                {fp.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ) : item.name === 'Motions' ? (
                    <li key={item.name}>
                      <button
                        onClick={() => setMotionsOpen(!motionsOpen)}
                        className={classNames(
                          'group flex w-full gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-indigo-600',
                          motionsOpen ? 'bg-blue-50 text-indigo-600' : ''
                        )}
                      >
                        <DocumentDuplicateIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600"/>
                        {item.name}
                        <ChevronDownIcon className={classNames('ml-auto w-5 h-5 transition-transform', motionsOpen ? 'rotate-180' : '')}/>
                      </button>
                      {motionsOpen && (
                        <ul className="mt-1 space-y-1">
                          {motionPages.map(mp => (
                            <li key={mp.name}>
                              <a
                                href={mp.href}
                                className={classNames(
                                  'group flex gap-x-3 pl-10 scale-95 rounded-md p-1 text-sm font-medium text-gray-600 hover:bg-blue-50 hover:text-indigo-600',
                                  location.pathname === mp.href ? 'bg-blue-100 text-indigo-600' : ''
                                )}
                              >
                                {mp.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ) : item.name === 'Letters' ? (
                    <li key={item.name}>
                      <button
                        onClick={() => setLettersOpen(!lettersOpen)}
                        className={classNames(
                          'group flex w-full gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-indigo-600',
                          lettersOpen ? 'bg-blue-50 text-indigo-600' : ''
                        )}
                      >
                        <EnvelopeIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600"/>
                        {item.name}
                        <ChevronDownIcon className={classNames('ml-auto w-5 h-5 transition-transform', lettersOpen ? 'rotate-180' : '')}/>
                      </button>
                      {lettersOpen && (
                        <ul className="mt-1 space-y-1">
                          {letterPages.map(lp => (
                            <li key={lp.name}>
                              <a
                                href={lp.href}
                                className={classNames(
                                  'group flex gap-x-3 pl-10 scale-95 rounded-md p-1 text-sm font-medium text-gray-600 hover:bg-blue-50 hover:text-indigo-600',
                                  location.pathname === lp.href ? 'bg-blue-100 text-indigo-600' : ''
                                )}
                              >
                                {lp.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ) : (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className={classNames(
                          'group flex gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-indigo-600',
                          location.pathname === item.href ? 'bg-blue-100 text-indigo-600' : ''
                        )}
                      >
                        <item.icon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600"/>
                        {item.name}
                      </a>
                    </li>
                  )
                ))}
              </ul>
            </li>
            <li className="mt-auto">
              <a href="/account" className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-indigo-600">
                <Cog6ToothIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600"/>
                Settings
              </a>
            </li>
          </ul>
        </nav>
      </div>

      {/* Top bar & Outlet unchanged */}
      <div className="lg:pl-72">
        <FirmTopBar onOpenSidebar={() => setSidebarOpen(true)} />

        {/* Quick Notes/Tasks floating button */}
        <button
          type="button"
          onClick={() => setQuickNotesOpen(true)}
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />
          Notes / Tasks
        </button>

        {/* Quick Notes/Tasks modal */}
        <Transition.Root show={quickNotesOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={setQuickNotesOpen}>
            <DialogBackdrop className="fixed inset-0 bg-black/30" />

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-6">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <DialogPanel className="relative w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                    <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                      <div>
                        <div className="text-base font-semibold text-gray-900">Quick Notes & Tasks</div>
                        <div className="text-sm text-gray-500">Add or update items without leaving your current page.</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setQuickNotesOpen(false)}
                        className="rounded-md p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      >
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="px-6 py-5">
                      <CaseHomeNotesTasks caseId={caseId} apiBase={API_URL} />
                    </div>
                  </DialogPanel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet context={{ caseInfo, loading, updateCaseInfo, refreshCaseInfo }}/>
          </div>
        </main>
      </div>
    </div>
  );
}
